// @LTG-ID: contact-step-email
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: source `Contact.html` lines 837-978 (v4),
//        ORCH-0087 point 1 (000000 sentinel verification),
//        CHG-0112 A1 (preview-banner env-gate)
// @CHANGED: 2026-07-06 session current (CHG-0112 · A1 — banner + sentinel
//           hint gated behind env var; sentinel accept-logic preserved)
//
// CHG-0083-M-A2 — Step 3: Email verification with 000000 sentinel.
//
// Four sub-stages now (was three in M-A1):
//   input    — email field with regex + disposable-domain validation
//   sending  — 800ms simulated delay with spinner + "Sending code to <email>…"
//   verify   — 6 digit cells, accept ONLY "000000", "Preview mode" banner
//   verified — animated checkmark + "Email confirmed" + email_verified_at stamp
//
// Changes from M-A1 (CHG-0083-M-A1) per ORCH-0087 point 1:
//   - Added "sending" sub-stage with 800ms delay (600-900ms range per spec).
//   - Verification tightened: accept ONLY "000000" (was: any 6 digits).
//   - Demo banner updated: "Preview mode — your code is 000000".
//   - Manual "Verify code" button added below cells (so "incomplete" error
//     has a triggerable surface; auto-verify still fires on 6th cell fill).
//   - Error states:
//       wrong_code → "That code doesn't match. Try `000000` in preview mode."
//       incomplete → "Enter all 6 digits."
//   - On success, stamps `email_verified_at = new Date().toISOString()` into
//     formData (per ORCH-0087 point 1).
//   - Resend restarts the flow (still 000000).

import { useEffect, useRef, useState } from "react";
import { validateEmail } from "./validators";

const DIGIT_PATTERN = /^\d?$/;
const SENDING_CODE_DELAY_MS = 800; // 600-900ms per ORCH-0087 point 1
const AUTO_VERIFY_DELAY_MS = 300; // perceptible "checking" beat
const SENTINEL_CODE = "000000";

// CHG-0112 A1 · Gate preview-only UI copy (banner + wrong-code hint) behind
// an env check. In production the sentinel accept-logic still works (a
// real 6-digit verify flow is future work, OQ-V6-8), but the string
// "000000" must never render for production visitors — it's a demo aid
// that reveals the sentinel and looks broken in a live product.
//
// Rendered only when:
//   - Vite/CRA dev build (NODE_ENV !== "production"), OR
//   - Explicit REACT_APP_ENABLE_PREVIEW_HELPERS=true env at build time.
const SHOW_PREVIEW_HELPERS =
    process.env.NODE_ENV !== "production" ||
    process.env.REACT_APP_ENABLE_PREVIEW_HELPERS === "true";

export const StepEmail = ({ data, setData, onNext, onBack }) => {
    const [stage, setStage] = useState(
        data.emailVerified ? "verified" : "input"
    );
    const [code, setCode] = useState(["", "", "", "", "", ""]);
    const [error, setError] = useState(null); // "incomplete" | "wrong_code" | null
    const [emailTouched, setEmailTouched] = useState(false);
    const cellRefs = useRef([]);
    const sendingTimerRef = useRef(null);
    const autoVerifyTimerRef = useRef(null);

    // Clean up timers on unmount.
    useEffect(() => {
        return () => {
            if (sendingTimerRef.current) clearTimeout(sendingTimerRef.current);
            if (autoVerifyTimerRef.current)
                clearTimeout(autoVerifyTimerRef.current);
        };
    }, []);

    const first = (data.firstName || "").trim();
    const emailCheck = validateEmail(data.email);

    const stampVerified = () => {
        setStage("verified");
        setData({
            ...data,
            emailVerified: true,
            email_verified_at: new Date().toISOString(),
        });
    };

    const sendCode = () => {
        setEmailTouched(true);
        if (!emailCheck.ok) return;
        setStage("sending");
        setError(null);
        setCode(["", "", "", "", "", ""]);
        sendingTimerRef.current = setTimeout(() => {
            setStage("verify");
            setTimeout(() => cellRefs.current[0]?.focus(), 100);
        }, SENDING_CODE_DELAY_MS);
    };

    const evaluateCode = (joined) => {
        if (joined.length < 6) {
            setError("incomplete");
            return;
        }
        if (joined !== SENTINEL_CODE) {
            setError("wrong_code");
            return;
        }
        setError(null);
        stampVerified();
    };

    const handleCellChange = (idx, val) => {
        if (!DIGIT_PATTERN.test(val)) return;
        const next = [...code];
        next[idx] = val;
        setCode(next);
        if (error) setError(null);
        if (val && idx < 5) cellRefs.current[idx + 1]?.focus();
        // Auto-verify when all 6 cells filled.
        if (next.every((c) => c.length === 1)) {
            const joined = next.join("");
            if (autoVerifyTimerRef.current)
                clearTimeout(autoVerifyTimerRef.current);
            autoVerifyTimerRef.current = setTimeout(() => {
                evaluateCode(joined);
            }, AUTO_VERIFY_DELAY_MS);
        }
    };

    const handleCellKey = (idx, e) => {
        if (e.key === "Backspace" && !code[idx] && idx > 0) {
            cellRefs.current[idx - 1]?.focus();
        } else if (e.key === "Enter") {
            evaluateCode(code.join(""));
        }
    };

    const resend = () => {
        setCode(["", "", "", "", "", ""]);
        setError(null);
        setStage("sending");
        sendingTimerRef.current = setTimeout(() => {
            setStage("verify");
            setTimeout(() => cellRefs.current[0]?.focus(), 100);
        }, SENDING_CODE_DELAY_MS);
    };

    const showEmailErr = emailTouched && !emailCheck.ok;

    const codeErrorMsg =
        error === "wrong_code"
            ? SHOW_PREVIEW_HELPERS
                ? "That code doesn't match. Try 000000 in preview mode."
                : "That code doesn't match. Check the code we sent and try again."
            : error === "incomplete"
              ? "Enter all 6 digits."
              : null;

    return (
        <section
            className="ltgc-step"
            data-testid="contact-step-email"
            aria-labelledby="ltgc-step-email-heading"
        >
            <span className="ltgc-step-greeting">
                Step 3 · Email verification
            </span>
            <h2 id="ltgc-step-email-heading">
                {first}, <em>let&apos;s confirm your email</em>.
            </h2>
            <p className="ltgc-sub">
                We&apos;ll send a 6-digit code to verify. Helps us reach you
                back and keeps the inbox clean of spam.
            </p>

            {stage === "input" && (
                <>
                    <div className="ltgc-field">
                        <label
                            className="ltgc-field-label"
                            htmlFor="ltgc-input-email"
                        >
                            Email
                        </label>
                        <input
                            id="ltgc-input-email"
                            className={
                                "ltgc-field-input " +
                                (showEmailErr ? "is-error" : "")
                            }
                            type="email"
                            autoFocus
                            inputMode="email"
                            autoComplete="email"
                            placeholder="sarah@company.com"
                            value={data.email}
                            aria-required="true"
                            aria-invalid={showEmailErr ? "true" : "false"}
                            aria-describedby={
                                showEmailErr
                                    ? "ltgc-input-email-error"
                                    : undefined
                            }
                            data-testid="contact-input-email"
                            onChange={(e) =>
                                setData({ ...data, email: e.target.value })
                            }
                            onBlur={() => setEmailTouched(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") sendCode();
                            }}
                        />
                        {showEmailErr && (
                            <span
                                id="ltgc-input-email-error"
                                className="ltgc-field-error"
                                role="alert"
                                data-testid="contact-error-email"
                            >
                                {emailCheck.msg}
                            </span>
                        )}
                    </div>
                    <div className="ltgc-step-nav">
                        <button
                            type="button"
                            className="ltgc-btn ltgc-btn-ghost"
                            onClick={onBack}
                            data-testid="contact-step-email-back"
                        >
                            ← Back
                        </button>
                        <button
                            type="button"
                            className="ltgc-btn ltgc-btn-primary"
                            disabled={!emailCheck.ok}
                            onClick={sendCode}
                            data-testid="contact-step-email-send-code"
                        >
                            Send verification code{" "}
                            <span className="ltgc-arrow">→</span>
                        </button>
                    </div>
                </>
            )}

            {stage === "sending" && (
                <div
                    className="ltgc-verify-sending"
                    role="status"
                    aria-live="polite"
                    aria-busy="true"
                    data-testid="contact-verify-sending"
                >
                    <div className="ltgc-envelope-wrap">
                        <svg
                            className="ltgc-envelope-svg ltgc-envelope-spin"
                            viewBox="0 0 96 96"
                            aria-hidden="true"
                        >
                            <circle
                                cx="48"
                                cy="48"
                                r="42"
                                fill="none"
                                stroke="var(--line-2)"
                                strokeWidth="3"
                            />
                            <circle
                                cx="48"
                                cy="48"
                                r="42"
                                fill="none"
                                stroke="var(--teal)"
                                strokeWidth="3"
                                strokeDasharray="60 200"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>
                    <p
                        className="ltgc-sub"
                        style={{ textAlign: "center", margin: "0 auto" }}
                    >
                        Sending verification code to{" "}
                        <strong>{data.email}</strong>…
                    </p>
                </div>
            )}

            {stage === "verify" && (
                <>
                    <div
                        className="ltgc-envelope-wrap"
                        data-testid="contact-envelope-wait"
                    >
                        <svg
                            className="ltgc-envelope-svg"
                            viewBox="0 0 96 96"
                            aria-hidden="true"
                        >
                            <g className="ltgc-envelope-body">
                                <rect
                                    x="14"
                                    y="34"
                                    width="68"
                                    height="46"
                                    rx="4"
                                    fill="#EAF8FA"
                                    stroke="#18B0BD"
                                    strokeWidth="2"
                                />
                                <path
                                    d="M14 38 L48 60 L82 38"
                                    fill="none"
                                    stroke="#18B0BD"
                                    strokeWidth="2"
                                    strokeLinejoin="round"
                                />
                            </g>
                            <g className="ltgc-envelope-flap">
                                <path
                                    d="M14 38 L48 16 L82 38 L82 42 L48 62 L14 42 Z"
                                    fill="#18B0BD"
                                />
                            </g>
                        </svg>
                    </div>
                    <p
                        className="ltgc-sub"
                        style={{
                            textAlign: "center",
                            margin: "0 auto 16px",
                        }}
                        data-testid="contact-verify-sent-line"
                    >
                        <strong>Code sent to {data.email}.</strong>
                        <br />
                        Enter the 6-digit code below.
                    </p>
                    <div
                        className="ltgc-verify-row"
                        role="group"
                        aria-label="6-digit verification code"
                        data-testid="contact-verify-row"
                    >
                        {code.map((c, i) => (
                            <input
                                key={i}
                                ref={(el) => (cellRefs.current[i] = el)}
                                className={
                                    "ltgc-verify-cell " +
                                    (c ? "is-filled " : "") +
                                    (error === "wrong_code" ? "is-error" : "")
                                }
                                type="text"
                                inputMode="numeric"
                                maxLength={1}
                                value={c}
                                aria-label={`Digit ${i + 1} of 6`}
                                aria-invalid={
                                    error === "wrong_code" ? "true" : "false"
                                }
                                data-testid={`contact-verify-cell-${i}`}
                                onChange={(e) =>
                                    handleCellChange(i, e.target.value)
                                }
                                onKeyDown={(e) => handleCellKey(i, e)}
                            />
                        ))}
                    </div>
                    {codeErrorMsg && (
                        <span
                            className="ltgc-field-error"
                            role="alert"
                            data-testid="contact-verify-error"
                            style={{
                                display: "block",
                                textAlign: "center",
                                margin: "10px auto 0",
                            }}
                        >
                            {codeErrorMsg}
                        </span>
                    )}
                    <div className="ltgc-verify-status">
                        Didn&apos;t receive it?
                        <button
                            type="button"
                            className="ltgc-resend"
                            onClick={resend}
                            data-testid="contact-verify-resend"
                        >
                            Resend
                        </button>
                    </div>
                    <p
                        className="ltgc-small-help is-demo"
                        style={{
                            display: SHOW_PREVIEW_HELPERS ? "block" : "none",
                            textAlign: "center",
                            margin: "16px auto 0",
                            maxWidth: 360,
                        }}
                        data-testid="contact-verify-demo-banner"
                        aria-hidden={SHOW_PREVIEW_HELPERS ? undefined : "true"}
                    >
                        Preview mode — your code is <strong>000000</strong>.
                    </p>
                    <div className="ltgc-step-nav">
                        <button
                            type="button"
                            className="ltgc-btn ltgc-btn-ghost"
                            onClick={() => {
                                setStage("input");
                                setError(null);
                                setCode(["", "", "", "", "", ""]);
                            }}
                            data-testid="contact-verify-change-email"
                        >
                            ← Change email
                        </button>
                        <button
                            type="button"
                            className="ltgc-btn ltgc-btn-primary"
                            onClick={() => evaluateCode(code.join(""))}
                            data-testid="contact-verify-submit-code"
                        >
                            Verify code <span className="ltgc-arrow">→</span>
                        </button>
                    </div>
                </>
            )}

            {stage === "verified" && (
                <>
                    <div
                        className="ltgc-envelope-wrap"
                        data-testid="contact-envelope-verified"
                    >
                        <svg
                            className="ltgc-envelope-svg"
                            viewBox="0 0 96 96"
                            aria-hidden="true"
                        >
                            <circle
                                cx="48"
                                cy="48"
                                r="42"
                                fill="#EAF8FA"
                                stroke="#22c55e"
                                strokeWidth="2"
                            />
                            <path
                                className="ltgc-envelope-checkmark"
                                d="M30 49 L43 62 L66 36"
                                fill="none"
                                stroke="#22c55e"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </div>
                    <p
                        className="ltgc-sub"
                        style={{ textAlign: "center", margin: "0 auto 8px" }}
                        data-testid="contact-verified-line"
                    >
                        <strong>{data.email}</strong> verified.
                    </p>
                    <p
                        className="ltgc-verify-status is-ok"
                        role="status"
                        aria-live="polite"
                    >
                        Email confirmed
                    </p>
                    <div className="ltgc-step-nav">
                        <button
                            type="button"
                            className="ltgc-btn ltgc-btn-ghost"
                            onClick={onBack}
                            data-testid="contact-step-email-back-verified"
                        >
                            ← Back
                        </button>
                        <button
                            type="button"
                            className="ltgc-btn ltgc-btn-primary"
                            onClick={onNext}
                            data-testid="contact-step-email-continue"
                        >
                            Continue <span className="ltgc-arrow">→</span>
                        </button>
                    </div>
                </>
            )}
        </section>
    );
};

export default StepEmail;
